/**
 * Le funzioni pure della pagina «Attività»: tutto ciò che si può sbagliare
 * senza che si veda subito.
 *
 * Stanno fuori dal componente perché sono l'unica parte di quella pagina che
 * si possa provare senza un browser — e sono anche l'unica dove un errore non
 * salta all'occhio: una riga con l'autore sbagliato, o l'ultima pagina che non
 * si apre, si notano solo a fine mese.
 *
 * ⚠️ Qui **non** si compone il testo di cosa è successo: `descrizione` arriva
 * già scritta dal backend, calcolata dai suoi `dettagli`, che non escono
 * dall'API. Ricomporla qui vorrebbe dire farsi mandare quei dettagli grezzi.
 */

export interface Autore {
  /** Sempre valorizzato: se manca il nome si ripiega sull'email. */
  nome: string;
  /** `null` quando l'etichetta non ne contiene una — es. i tentativi anonimi. */
  email: string | null;
}

/**
 * `"Enrico Testa <e.testa@azienda.it>"` → nome ed email separati.
 *
 * Il registro congela l'autore in **una** stringa, perché quella stringa deve
 * sopravvivere alla cancellazione dell'utente. In tabella però il nome e
 * l'indirizzo non hanno lo stesso peso: il nome è quello che si legge, l'email
 * serve solo a distinguere due colleghi che si chiamano allo stesso modo.
 *
 * Quando l'etichetta non ha la forma prevista — un tentativo di accesso
 * anonimo, o un'etichetta scritta diversamente da una versione futura del
 * backend — si restituisce così com'è: mostrare tutta la riga è sempre meglio
 * che mostrarne metà.
 */
export function separaAutore(etichetta: string): Autore {
  const testo = etichetta.trim();

  // Gli spazi di troppo li mangia l'espressione, dentro e fuori dalle
  // parentesi angolari: ripulire dopo, a mano, darebbe due righe che non
  // possono sbagliare — perché a quel punto non resterebbe niente da ripulire.
  const trovato = /^(.*?)\s*<\s*([^<>]+?)\s*>$/.exec(testo);
  if (trovato === null) return { nome: testo, email: null };

  const nome = trovato[1] ?? '';
  const email = trovato[2] ?? '';

  // `<solo@indirizzo.it>` senza nome davanti: la colonna «Chi» non può
  // restare vuota, quindi l'indirizzo fa da nome e non si ripete accanto.
  return nome === '' ? { nome: email, email: null } : { nome, email };
}

/**
 * `"ACCESSO_FALLITO"` → `"Accesso fallito"`.
 *
 * Il codice dell'azione è dell'applicazione, non di chi legge: in tabella ci
 * va la parola, non la costante. Non c'è nessun elenco di codici da tenere
 * allineato — si trasforma qualunque cosa arrivi — proprio perché un'azione
 * aggiunta domani nel backend non deve comparire qui come un buco.
 */
export function etichettaAzione(azione: string): string {
  // Trattini bassi e spazi contano come uno stesso separatore, e più di fila
  // valgono per uno: `CREA__UTENTE` è «Crea utente», non «Crea  utente».
  const parole = azione.toLowerCase().replace(/[_\s]+/g, ' ').trim();

  // Nessun caso speciale per la stringa vuota: `''.charAt(0)` è `''`, e la
  // riga qui sotto la restituisce vuota da sé. Scriverne uno apposta darebbe
  // un ramo che non può essere sbagliato perché non fa niente.
  return parole.charAt(0).toUpperCase() + parole.slice(1);
}

/**
 * Di che colore va la riga.
 *
 * ⚠️ **Ciò che non si riconosce è `normale`**, e non il contrario: un codice
 * nuovo, arrivato da una versione più recente del backend, deve comparire
 * spento. Se il caso ignoto finisse in rosso, ogni funzionalità aggiunta si
 * annuncerebbe come un allarme, e dopo tre volte nessuno guarderebbe più i
 * rossi veri.
 */
export function tonoAzione(azione: string): 'normale' | 'distruttiva' | 'fallita' {
  const codice = azione.toUpperCase();
  // Prima i fallimenti: un ipotetico `ELIMINA_FALLITA` non è una
  // cancellazione avvenuta, ed è quella la cosa da non dire.
  if (codice.includes('FALLIT') || codice.includes('NEGAT')) return 'fallita';
  if (codice.includes('ELIMINA') || codice.includes('CANCELLA')) return 'distruttiva';
  return 'normale';
}

/**
 * Quante pagine servono per `totale` righe.
 *
 * Con zero righe la risposta è **una**, non zero: la pagina vuota esiste e si
 * mostra, ed è proprio quella che si vedrà per prima ora che il registro è
 * stato svuotato.
 */
export function numeroPagine(totale: number, perPagina: number): number {
  if (perPagina <= 0) return 1;
  return Math.max(1, Math.ceil(totale / perPagina));
}

/**
 * `"101–112 di 128"` — quali righe si stanno guardando.
 *
 * `mostrati` è quante ne è arrivate davvero, non `perPagina`: sull'ultima
 * pagina sono di meno, e scrivere «101–150 di 128» sarebbe insieme sbagliato e
 * ridicolo.
 */
export function descriviIntervallo(
  pagina: number,
  perPagina: number,
  mostrati: number,
  totale: number,
): string {
  if (mostrati <= 0) return `0 di ${totale}`;
  const primo = (pagina - 1) * perPagina + 1;
  return `${primo}–${primo + mostrati - 1} di ${totale}`;
}
